import React, { useCallback, useState, useEffect } from 'react';
import Navigation from '../Navbar/Navbar';
import axios from 'axios';
import {
    Container, Row, Col, Card, Button, Table, Modal, Form,
    Badge, Alert, Spinner, InputGroup, Dropdown, ButtonGroup
} from 'react-bootstrap';
import {
    Plus, Dash, PencilSquare,
    ExclamationTriangle, CheckCircle, Search,
    ArrowRepeat, Clock, BoxSeam, Download, PeopleFill, Grid3x3Gap
} from 'react-bootstrap-icons';
import './StockManagement.css';

const EQUIPMENT_TYPES = [
    "Computer",
    "Laptop",
    "Monitor",
    "Router/Managed Switch",
    "Mouse",
    "Keyboard",
    "Ethernet Cable",
    "USB A-B Cable",
    "USB Headset",
    "VGA Cable / Display Cable",
    "DVI Cable / Display Cable",
    "Power Cable",
    "DisplayPort Cable",
    "PC's Power Cable"
];

const apiUrl = "/api";

function StockManagement() {
    const [stockItems, setStockItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showAdjustModal, setShowAdjustModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [history, setHistory] = useState([]);
    const [alert, setAlert] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [lowStockOnly, setLowStockOnly] = useState(false);
    const [kitCapacity, setKitCapacity] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '', category: 'General', sku: '', quantity: 0,
        min_stock: 10, unit: 'pcs', location: '', notes: '', cost_per_unit: 0
    });

    // Adjustment form state
    const [adjustData, setAdjustData] = useState({
        adjustment_type: 'add', quantity: 0, reason: ''
    });

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
            const headers = { Authorization: `Bearer ${token}` };

            let url = `${apiUrl}/stock?tenant_id=${tenantId}`;
            if (categoryFilter) url += `&category=${categoryFilter}`;
            if (lowStockOnly) url += `&low_stock=true`;

            const [itemsRes, categoriesRes] = await Promise.all([
                axios.get(url, { headers }),
                axios.get(`${apiUrl}/stock-categories?tenant_id=${tenantId}`, { headers })
            ]);
            const capacityRes = await axios.get(`${apiUrl}/stock/new-hire-capacity?tenant_id=${tenantId}`, { headers });

            setStockItems(itemsRes.data.data);
            setCategories(categoriesRes.data);
            setKitCapacity(capacityRes.data);
        } catch (error) {
            console.error('Error fetching stock data:', error);
            setAlert({ type: 'danger', message: 'Failed to load stock data' });
        } finally {
            setLoading(false);
        }
    }, [categoryFilter, lowStockOnly]);

    useEffect(() => {
        if (!localStorage.getItem('token')) {
            window.location.href = '#/login';
            return;
        }
        fetchData();
    }, [fetchData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
            const headers = { Authorization: `Bearer ${token}` };

            const payload = { ...formData, tenant_id: tenantId };

            if (editingItem && editingItem.id) {
                // Update existing item
                await axios.put(`${apiUrl}/stock/${editingItem.id}`, payload, { headers });
                setAlert({ type: 'success', message: 'Stock details updated successfully' });
            } else {
                // Create new item (or "Save" a virtual item)
                // Ensure name and category match if we are in fixed-list mode
                if (editingItem && editingItem.is_virtual) {
                    payload.category = editingItem.name; // Enforce category matches name
                    payload.name = editingItem.name;
                }

                await axios.post(`${apiUrl}/stock`, payload, { headers });
                setAlert({ type: 'success', message: 'Stock item initialized successfully' });
            }

            setShowModal(false);
            resetForm();
            fetchData();
        } catch (error) {
            setAlert({ type: 'danger', message: error.response?.data?.error || 'Error saving stock item' });
        }
    };

    const handleAdjust = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
            const headers = { Authorization: `Bearer ${token}` };

            if (!selectedItem.id) {
                // Virtual Item: Create it first with the directed quantity
                let initialQty = 0;
                if (adjustData.adjustment_type === 'add') initialQty = adjustData.quantity;
                else if (adjustData.adjustment_type === 'set') initialQty = adjustData.quantity;

                // Construct creation payload
                const payload = {
                    name: selectedItem.name,
                    category: selectedItem.category, // or selectedItem.name
                    quantity: initialQty,
                    min_stock: selectedItem.min_stock,
                    unit: 'pcs',
                    tenant_id: tenantId,
                    // use defaults
                    location: '',
                    notes: adjustData.reason
                };

                await axios.post(`${apiUrl}/stock`, payload, { headers });
                setAlert({ type: 'success', message: 'Stock initialized and adjusted' });

            } else {
                // Normal adjustment
                await axios.post(`${apiUrl}/stock/${selectedItem.id}/adjust`, adjustData, { headers });
                setAlert({ type: 'success', message: 'Stock adjusted successfully' });
            }

            setShowAdjustModal(false);
            setAdjustData({ adjustment_type: 'add', quantity: 0, reason: '' });
            fetchData();
        } catch (error) {
            setAlert({ type: 'danger', message: error.response?.data?.error || 'Error adjusting stock' });
        }
    };

    const fetchHistory = async (item) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${apiUrl}/stock/${item.id}/history`,
                { headers: { Authorization: `Bearer ${token}` } });
            setHistory(res.data);
            setSelectedItem(item);
            setShowHistoryModal(true);
        } catch (error) {
            setAlert({ type: 'danger', message: 'Error fetching history' });
        }
    };

    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({
            name: item.name, category: item.category, sku: item.sku || '',
            quantity: item.quantity, min_stock: item.min_stock,
            unit: item.unit, location: item.location || '', notes: item.notes || '',
            cost_per_unit: item.cost_per_unit || 0
        });
        setShowModal(true);
    };

    const openAdjustModal = (item) => {
        setSelectedItem(item);
        setAdjustData({ adjustment_type: 'add', quantity: 0, reason: '' });
        setShowAdjustModal(true);
    };

    const openAddStockModal = () => {
        resetForm();
        setFormData({
            name: 'Ethernet Cable',
            category: 'Ethernet Cable',
            sku: '',
            quantity: 0,
            min_stock: 10,
            unit: 'pcs',
            location: '',
            notes: '',
            cost_per_unit: 0
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setEditingItem(null);
        setFormData({ name: '', category: 'General', sku: '', quantity: 0, min_stock: 10, unit: 'pcs', location: '', notes: '', cost_per_unit: 0 });
    };

    const handleExportCode = () => {
        const headers = ['ID', 'Name', 'Category', 'Quantity', 'Unit', 'Location', 'Status'];
        const rows = stockItems.map(item => [
            item.id,
            `"${item.name.replace(/"/g, '""')}"`, // Escape quotes
            item.category,
            item.quantity,
            item.unit,
            item.location || '',
            item.quantity <= item.min_stock ? 'Low' : 'OK'
        ]);

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stock-inventory-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    // Calculate Totals
    const bottleneck = kitCapacity?.bottlenecks?.[0];

    const filteredItems = stockItems.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStockStatus = (item) => {
        const ratio = item.quantity / item.min_stock;
        if (ratio <= 0.5) return { variant: 'danger', text: 'Critical' };
        if (ratio <= 1) return { variant: 'warning', text: 'Low' };
        return { variant: 'success', text: 'OK' };
    };

    if (loading) {
        return (
            <div><Navigation />
                <Container className="mt-4 text-center py-5">
                    <Spinner animation="border" variant="primary" /><p className="mt-3">Loading stock data...</p>
                </Container>
            </div>
        );
    }

    return (
        <div>
            <Navigation />
            <Container className="stock-container page-content-wrapper wide py-4">
                <Row className="mb-4 align-items-center g-3">
                    <Col>
                        <h1 className="page-title mb-0"><BoxSeam className="me-3" />Stock + Inventory</h1>
                    </Col>
                    <Col xs="auto" className="d-flex gap-2">
                        <Button variant="outline-primary" href="/#/inventory">
                            <Grid3x3Gap className="me-2" />View Inventory
                        </Button>
                        <Dropdown as={ButtonGroup} align="end">
                            <Button variant="success" onClick={openAddStockModal}>
                                <Plus className="me-2" />Add
                            </Button>
                            <Dropdown.Toggle split variant="success" id="stock-add-dropdown" />
                            <Dropdown.Menu>
                                <Dropdown.Item href="/#/inv">
                                    <BoxSeam className="me-2" />Tracked Equipment with QR-ID
                                </Dropdown.Item>
                                <Dropdown.Item onClick={openAddStockModal}>
                                    <Plus className="me-2" />Count Stock Quantity
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Col>
                </Row>

                {alert && <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>{alert.message}</Alert>}

                {kitCapacity && (
                    <Row className="mb-4 g-3">
                        <Col lg={4} md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <p className="text-muted text-uppercase small mb-1">New Hire Readiness</p>
                                            <h2 className="mb-0">{kitCapacity.new_hire_capacity}</h2>
                                            <p className="mb-0 text-muted">complete kit{kitCapacity.new_hire_capacity === 1 ? '' : 's'} available</p>
                                        </div>
                                        <PeopleFill size={28} className="text-primary" />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={4} md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <p className="text-muted text-uppercase small mb-1">Limiting Item</p>
                                    {bottleneck ? (
                                        <>
                                            <h5 className="mb-1">{bottleneck.stock_item_name}</h5>
                                            <p className="mb-0 text-muted">
                                                {bottleneck.quantity} on hand, {bottleneck.required_qty} needed per hire
                                            </p>
                                        </>
                                    ) : (
                                        <p className="mb-0 text-muted">No kit requirements configured</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <p className="text-muted text-uppercase small mb-2">Tracking Start</p>
                                    <p className="mb-2">
                                        {kitCapacity.tracking_start_at
                                            ? new Date(kitCapacity.tracking_start_at).toLocaleString()
                                            : 'Not configured'}
                                    </p>
                                    <Badge bg={kitCapacity.tracking_enabled ? 'success' : 'secondary'}>
                                        {kitCapacity.tracking_enabled ? 'Tracking enabled' : 'Tracking disabled'}
                                    </Badge>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={12}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">Required Equipment Per New Hire</h5>
                                        <Badge bg={bottleneck && bottleneck.capacity === 0 ? 'danger' : 'info'}>
                                            bottleneck: {bottleneck?.stock_item_name || 'none'}
                                        </Badge>
                                    </div>
                                    <Row className="g-2">
                                        {kitCapacity.requirements.map(item => (
                                            <Col xl={3} lg={4} md={6} key={item.stock_item_name}>
                                                <div className={`p-2 border rounded ${item.capacity === kitCapacity.new_hire_capacity ? 'border-danger bg-light' : ''}`}>
                                                    <div className="d-flex justify-content-between">
                                                        <strong>{item.stock_item_name}</strong>
                                                        <span className="text-muted">x{item.required_qty}</span>
                                                    </div>
                                                    <div className="small text-muted">
                                                        {item.quantity} available = {item.capacity} hire{item.capacity === 1 ? '' : 's'}
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Filters */}
                <Row className="mb-4">
                    <Col md={4}>
                        <InputGroup>
                            <InputGroup.Text><Search /></InputGroup.Text>
                            <Form.Control placeholder="Search items..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </InputGroup>
                    </Col>
                    <Col md={3}>
                        <Form.Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                            <option value="">All Categories</option>
                            {categories.map((cat, i) => <option key={i} value={cat.category}>{cat.category} ({cat.item_count})</option>)}
                        </Form.Select>
                    </Col>
                    <Col md={3}>
                        <Form.Check type="switch" label="Show Low Stock Only" checked={lowStockOnly}
                            onChange={(e) => setLowStockOnly(e.target.checked)} className="mt-2" />
                    </Col>
                    <Col md={2} className="text-end">
                        <Button variant="outline-success" className="me-2" onClick={handleExportCode} title="Export CSV">
                            <Download />
                        </Button>
                        <Button variant="outline-secondary" onClick={fetchData}><ArrowRepeat className="me-2" />Refresh</Button>
                    </Col>
                </Row>

                {/* Stock Table */}
                <Card>
                    <Card.Body className="p-0">
                        <Table hover className="stock-table mb-0">
                            <thead>
                                <tr>
                                    <th>Item Type</th>
                                    <th className="text-center">Quantity</th>
                                    <th className="text-center">Min Stock</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.length === 0 ? (
                                    <tr><td colSpan="5" className="text-center py-4 text-muted">No stock items found.</td></tr>
                                ) : filteredItems.map(item => {
                                    const status = getStockStatus(item);
                                    return (
                                        <tr key={item.name} className={item.quantity <= item.min_stock ? 'low-stock-row' : ''}>
                                            <td><strong>{item.name}</strong></td>
                                            <td className="text-center">
                                                <span className={`qty-display ${status.variant}`}>{item.quantity}</span>
                                            </td>
                                            <td className="text-center text-muted">{item.min_stock}</td>
                                            <td className="text-center">
                                                <Badge bg={status.variant}>
                                                    {status.variant === 'danger' && <ExclamationTriangle className="me-1" />}
                                                    {status.variant === 'success' && <CheckCircle className="me-1" />}
                                                    {status.text}
                                                </Badge>
                                            </td>
                                            <td className="text-center">
                                                <Button variant="outline-success" size="sm" className="me-1" onClick={() => openAdjustModal(item)} title="Adjust Stock">
                                                    <Plus /><Dash /> Adjust
                                                </Button>
                                                <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(item)} title="Edit Details">
                                                    <PencilSquare />
                                                </Button>
                                                {!item.is_virtual && (
                                                    <Button variant="outline-info" size="sm" className="me-1" onClick={() => fetchHistory(item)} title="History">
                                                        <Clock />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                {/* Add/Edit Modal */}
                <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{editingItem ? 'Edit Stock Item' : 'Add Count Stock Quantity'}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Item Type *</Form.Label>
                                        {editingItem ? (
                                            <Form.Control required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                        ) : (
                                            <Form.Select
                                                required
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value, category: e.target.value })}
                                            >
                                                {EQUIPMENT_TYPES.map((type, i) => (
                                                    <option key={i} value={type}>{type}</option>
                                                ))}
                                            </Form.Select>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Category (Equipment Type)</Form.Label>
                                        <Form.Select
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        >
                                            <option value="General">General</option>
                                            {EQUIPMENT_TYPES.map((type, i) => (
                                                <option key={i} value={type}>{type}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Quantity</Form.Label>
                                        <Form.Control type="number" min="0" value={formData.quantity}
                                            onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                                            disabled={editingItem} />
                                        {editingItem && <Form.Text className="text-muted">Use Adjust Stock to change quantity</Form.Text>}
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Min Stock</Form.Label>
                                        <Form.Control type="number" min="0" value={formData.min_stock}
                                            onChange={(e) => setFormData({ ...formData, min_stock: parseInt(e.target.value) || 0 })} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Unit</Form.Label>
                                        <Form.Select value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })}>
                                            <option value="pcs">Pieces</option>
                                            <option value="box">Box</option>
                                            <option value="pack">Pack</option>
                                            <option value="roll">Roll</option>
                                            <option value="set">Set</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>SKU</Form.Label>
                                        <Form.Control value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Location</Form.Label>
                                <Form.Control value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                    placeholder="e.g., Warehouse A, Shelf 3" />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control as="textarea" rows={2} value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                            <Button variant="primary" type="submit">{editingItem ? 'Update' : 'Create'}</Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Adjust Stock Modal */}
                <Modal show={showAdjustModal} onHide={() => setShowAdjustModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Adjust Stock: {selectedItem?.name}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleAdjust}>
                        <Modal.Body>
                            <div className="text-center mb-4">
                                <h4>Current Quantity: <Badge bg="primary" className="fs-5">{selectedItem?.quantity}</Badge></h4>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>Adjustment Type</Form.Label>
                                <div className="d-flex gap-2">
                                    {['add', 'remove', 'set'].map(type => (
                                        <Button key={type} variant={adjustData.adjustment_type === type ? 'primary' : 'outline-primary'}
                                            onClick={() => setAdjustData({ ...adjustData, adjustment_type: type })} className="flex-fill">
                                            {type === 'add' && <Plus className="me-1" />}
                                            {type === 'remove' && <Dash className="me-1" />}
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </Button>
                                    ))}
                                </div>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{adjustData.adjustment_type === 'set' ? 'New Quantity' : 'Quantity to ' + adjustData.adjustment_type}</Form.Label>
                                <Form.Control type="number" min="0" required value={adjustData.quantity}
                                    onChange={(e) => setAdjustData({ ...adjustData, quantity: parseInt(e.target.value) || 0 })} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Reason</Form.Label>
                                <Form.Control as="textarea" rows={2} value={adjustData.reason}
                                    onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
                                    placeholder="e.g., Received new shipment, Used for project X" />
                            </Form.Group>
                            {adjustData.quantity > 0 && (
                                <Alert variant="info">
                                    New quantity will be: <strong>
                                        {adjustData.adjustment_type === 'add' ? (selectedItem?.quantity || 0) + adjustData.quantity :
                                            adjustData.adjustment_type === 'remove' ? Math.max(0, (selectedItem?.quantity || 0) - adjustData.quantity) :
                                                adjustData.quantity}
                                    </strong>
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowAdjustModal(false)}>Cancel</Button>
                            <Button variant="success" type="submit">Apply Adjustment</Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* History Modal */}
                <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Stock History: {selectedItem?.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {history.length === 0 ? (
                            <p className="text-muted text-center">No history available</p>
                        ) : (
                            <Table striped size="sm">
                                <thead>
                                    <tr><th>Date</th><th>Type</th><th>Change</th><th>Qty Before</th><th>Qty After</th><th>Reason</th><th>By</th></tr>
                                </thead>
                                <tbody>
                                    {history.map((h, i) => (
                                        <tr key={i}>
                                            <td>{new Date(h.created_at).toLocaleString()}</td>
                                            <td><Badge bg={h.adjustment_type === 'add' ? 'success' : h.adjustment_type === 'remove' ? 'danger' : 'info'}>{h.adjustment_type}</Badge></td>
                                            <td className={h.quantity_change >= 0 ? 'text-success' : 'text-danger'}>{h.quantity_change >= 0 ? '+' : ''}{h.quantity_change}</td>
                                            <td>{h.previous_qty}</td>
                                            <td>{h.new_qty}</td>
                                            <td>{h.reason || '-'}</td>
                                            <td>{h.user_name}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Modal.Body>
                </Modal>
            </Container>
        </div>
    );
}

export default StockManagement;
